import PropTypes from 'prop-types';
import { useMemo, useRef, useState } from 'react';
import { alpha, keyframes, styled } from '@mui/material/styles';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useTheme } from '@mui/material/styles';
import MenuPopover from './MenuPopover';
import useMyChallenges from '../../Hooks/useMyChallenges';
import useIntervalTargets from '../../Hooks/useIntervalTargets';
import {
  CHALLENGE_TYPE_LABELS,
  formatChallengeValue,
  formatGap,
  formatUpdatedAt,
} from '../../utils/challengeFormat';

/**
 * Toolbar entry point for challenge progress.
 *
 * Collapsed it is a pill with the active-challenge count; clicking expands a
 * scrolling panel of one row per challenge, and "Full view" hands off to
 * Interval Targets over SSO.
 *
 * Outlined rather than filled on purpose: DataAccessButton is the only filled
 * control in this toolbar, which is what marks it as the thing that leaves the
 * portal. This one is a status indicator first and a door second, so it reads
 * quieter.
 */

/**
 * Filled and coloured, because a target you are behind on should catch the eye
 * on the way past — a quiet outline in a busy toolbar gets scanned over.
 *
 * The pill is its own progress bar: a white rule along the bottom edge fills to
 * the percentage, so the number and the shape say the same thing.
 */
/**
 * Where a partitioned challenge is: "Day 4 of 10", "Week 2 of 3", "Round 2 of 5".
 * Null for a challenge judged once over its whole duration.
 */
function roundPosition(partition) {
  if (!partition?.current) return null;
  const index = (partition.rounds || []).findIndex(
    (r) => r.partition_start === partition.current.partition_start,
  );
  const noun =
    partition.type === 'daily' ? 'Day' : partition.type === 'weekly' ? 'Week' : 'Round';
  return `${noun} ${index + 1} of ${partition.total_rounds}`;
}

/** "today", "this week" or "this round" — what the figures on a round cover. */
const roundSpan = (partition) =>
  partition?.type === 'daily' ? 'today' : partition?.type === 'weekly' ? 'this week' : 'this round';

/** Below this, the progress bar reads as behind. */
const BEHIND_THRESHOLD = 50;

/**
 * A strong red pulse around the badge, so a live target is the first thing the
 * eye lands on in the toolbar (asked for 2026-10-07: "eye catchy"). Two layers:
 * a bright glow that swells and fades, and a ring that pushes outward with it.
 */
const glowPulse = (glowColor) => keyframes`
  0%, 100% {
    box-shadow: 0 0 6px 1px ${alpha(glowColor, 0.55)}, 0 0 0 0 ${alpha(glowColor, 0.6)};
  }
  50% {
    box-shadow: 0 0 22px 7px ${alpha(glowColor, 0.95)}, 0 0 0 6px ${alpha(glowColor, 0)};
  }
`;

const Pill = styled(ButtonBase, {
  shouldForwardProp: (prop) =>
    !['compact', 'tone', 'ink', 'bar', 'percent', 'showBar', 'glow'].includes(prop),
})(({ theme, compact, tone, ink, bar, percent, showBar, glow }) => ({
  position: 'relative',
  overflow: 'hidden',
  height: 34,
  minWidth: compact ? 34 : 'auto',
  width: compact ? 34 : 'auto',
  padding: compact ? 0 : '0 13px',
  borderRadius: 999,
  gap: 7,
  fontFamily: theme.typography.fontFamily,
  fontSize: 12.5,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  // Derived rather than hardcoded white: amber needs dark text to be legible,
  // green needs light. Letting MUI pick means the tone can change without
  // quietly making the label unreadable.
  color: ink,
  backgroundColor: tone,
  boxShadow: `0 1px 2px ${alpha('#101828', 0.18)}`,
  transition: theme.transitions.create(['filter', 'transform', 'box-shadow'], { duration: 160 }),
  // Only while there is a challenge to chase — the grey "no challenge" badge
  // has nothing to call attention to.
  animation: glow ? `${glowPulse(theme.palette.error.main)} 1.6s ease-in-out infinite` : 'none',

  '&:hover': {
    filter: 'brightness(1.07)',
    transform: 'translateY(-1px)',
    boxShadow: `0 6px 18px -4px ${alpha(tone, 0.55)}, 0 1px 2px ${alpha('#101828', 0.2)}`,
  },
  '&:active': { transform: 'translateY(0)', filter: 'brightness(0.97)' },
  '&.Mui-focusVisible': { outline: `2px solid ${alpha(tone, 0.9)}`, outlineOffset: 2 },

  // The unfilled track, so the bar reads as a proportion rather than a stub.
  // Absent when there is no challenge: an empty track reads as 0% of something,
  // and the badge is then a door rather than a status.
  '&::before': {
    content: showBar ? '""' : 'none',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 4,
    backgroundColor: alpha(ink, 0.22),
  },

  // The progress rule — red or green, which is the only part that changes with
  // how you are doing. The pill itself stays one colour so the badge is always
  // recognisably the same thing in the toolbar.
  '&::after': {
    content: showBar ? '""' : 'none',
    position: 'absolute',
    left: 0,
    bottom: 0,
    height: 4,
    width: `${Math.max(0, Math.min(100, percent))}%`,
    backgroundColor: bar,
    transition: 'width .4s ease',
  },

  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    animation: 'none',
    '&:hover': { transform: 'none' },
    '&::after': { transition: 'none' },
  },
}));

/**
 * Says which kind of challenge a row is, so "of ₹8,00,000" is never ambiguous
 * about whose number it is — yours, or the whole team's combined.
 */
const KindChip = styled('span', {
  shouldForwardProp: (prop) => prop !== 'kind',
})(({ theme, kind }) => {
  const tone = kind === 'group' ? theme.palette.info : theme.palette.primary;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    height: 16,
    padding: '0 6px',
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: tone.dark,
    backgroundColor: alpha(tone.main, 0.12),
  };
});

function ChallengeRow({ challenge }) {
  const achieved = formatChallengeValue(challenge.achieved, challenge.type);
  // The top of the ladder: the bar runs from nothing to here.
  const target = formatChallengeValue(challenge.topTarget ?? challenge.against, challenge.type);
  const done = challenge.ladderComplete;
  const next = challenge.nextTier;
  const percent = Math.max(0, Math.min(100, Number(challenge.percent) || 0));
  // Round by round, the figures are this round's — say which, and how the
  // rounds before it went, or "of ₹20,000" reads as the whole challenge's.
  const round = roundPosition(challenge.partition);

  return (
    <Box sx={{ px: 1.5, py: 1.25 }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle2" noWrap sx={{ fontSize: 13 }}>
          {challenge.name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
          {percent}%
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
        <KindChip kind={challenge.kind}>
          {challenge.kind === 'group' ? 'Team' : 'Individual'}
        </KindChip>
        <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
          {CHALLENGE_TYPE_LABELS[challenge.type] || challenge.type} · {challenge.assignmentLabel}
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mt: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
          {achieved}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          of {target}
          {round ? ` ${roundSpan(challenge.partition)}` : ''}
          {/* A head's figure is their team's, not their own selling. */}
          {challenge.fromTeam ? ` · from your team of ${challenge.fromTeam}` : ''}
        </Typography>
      </Stack>

      <LinearProgress
        variant="determinate"
        value={percent}
        color={done ? 'success' : 'primary'}
        sx={{ mt: 0.75, height: 5, borderRadius: 999 }}
      />

      {/* The one prize held, then the one being chased — prizes do not add
          up, so there is never a list here. */}
      <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Achieved
        </Typography>
        <Typography
          variant="caption"
          noWrap
          sx={{ fontWeight: 600, color: challenge.prizeWon ? 'success.main' : 'text.disabled' }}
        >
          {challenge.prizeWon || 'None yet'}
        </Typography>
      </Stack>
      {next && (
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Current target
          </Typography>
          <Typography variant="caption" noWrap>
            {next.prize} ·{' '}
            {formatGap(Math.max(0, Number(next.target) - challenge.achieved), challenge.type)}
          </Typography>
        </Stack>
      )}

      {round && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
          {round} · won {challenge.partition.rounds_won} of {challenge.partition.rounds_played} so far
        </Typography>
      )}
    </Box>
  );
}

ChallengeRow.propTypes = { challenge: PropTypes.object.isRequired };

export default function TargetsBadge({ compact = false }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const { data, loading, updatedAt, refresh } = useMyChallenges();
  const { loading: opening, open: openTargets } = useIntervalTargets();

  const theme = useTheme();

  // Individual first, then group — the order §7 specifies.
  //
  // `data.team` is deliberately left out. Those are the viewer's team members'
  // own individual challenges: the viewer is not measured on them, so counting
  // them here would inflate the badge with other people's targets. They belong
  // in the full view, which is built to show them as rows per person.
  const rows = useMemo(() => {
    if (!data) return [];
    return [
      ...(data.individual || []).map((c) => ({ ...c, kind: 'individual' })),
      ...(data.group || []).map((c) => ({ ...c, kind: 'group' })),
    ];
  }, [data]);

  /**
   * One challenge on the pill, not a count — a number of challenges says
   * nothing about how you are doing.
   *
   * Individual beats group: it is the one you personally answer for. Within a
   * bucket the largest target wins, on the reasoning that clearing the biggest
   * number clears the smaller ones on the way.
   */
  const featured = useMemo(() => {
    if (!data) return null;
    const pool = data.individual?.length ? data.individual : data.group || [];
    if (!pool.length) return null;
    return pool.reduce((best, row) => (row.against > best.against ? row : best));
  }, [data]);

  // The badge renders even with nothing to report, because it is the only way
  // into Interval Targets — hiding it on an empty month would leave the portal
  // unreachable. With no challenge it is simply a labelled door.
  //
  // It does NOT render for somebody a challenge can never name: Finance, who
  // set them rather than run in them, and roles outside the six families. The
  // server says which, on `eligible` — the badge holds no list of its own, so
  // there is nothing here to drift out of step with the assignment rules.
  const done = featured ? featured.ladderComplete : false;
  // Clamped for the bar; `featured.percent` stays raw so 116% still reads as 116%.
  const percent = featured ? Math.max(0, Math.min(100, Number(featured.percent) || 0)) : 0;

  // Amber while there is a target to chase — it is the badge's identity, and a
  // control that changes colour is a control you have to re-find. Only the bar
  // carries the state: red below half, green from half up.
  //
  // Red is the mid tone rather than the dark one: against amber the dark red
  // read almost brown. Green stays dark, which it needs to separate from the
  // amber behind it.
  //
  // With no challenge it drops to a neutral grey: amber is a call to act, and
  // there is nothing to act on.
  const tone = featured ? theme.palette.warning.main : theme.palette.grey[300];
  const ink = theme.palette.getContrastText(tone);
  const bar = percent < BEHIND_THRESHOLD ? theme.palette.error.main : theme.palette.success.dark;
  const featuredRound = featured ? roundPosition(featured.partition) : null;
  const label = featured
    ? `${featured.name}: ${featured.percent}% of target${featuredRound ? ` (${featuredRound})` : ''}`
    : 'Targets — no challenges for you right now';

  const pill = (
    <Pill
      ref={anchorRef}
      compact={compact ? 1 : 0}
      tone={tone}
      ink={ink}
      bar={bar}
      percent={percent}
      showBar={featured ? 1 : 0}
      glow={featured ? 1 : 0}
      onClick={() => setOpen(true)}
      aria-label={`${label}. Open your challenge progress`}
      aria-haspopup="dialog"
      aria-expanded={open}
    >
      <Icon icon={done ? 'mdi:trophy-variant' : 'mdi:target-arrow'} width={17} />
      {!compact &&
        (featured ? (
          <>
            {/* Round by round, the pill is today's round — it resets each round,
                so it says so rather than reading as the whole challenge. */}
            <span>
              {done
                ? featuredRound
                  ? `Won ${roundSpan(featured.partition)}`
                  : 'Achieved'
                : `${featured.percent}%${featuredRound ? ` ${roundSpan(featured.partition)}` : ''}`}
            </span>
            {/* The name is what makes the number mean something, but it is the
                first thing to go when the toolbar runs out of room. */}
            <Box
              component="span"
              sx={{
                display: { xs: 'none', lg: 'inline' },
                maxWidth: 132,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                opacity: 0.9,
                fontWeight: 500,
              }}
            >
              {featured.name}
            </Box>
          </>
        ) : (
          <span>Targets</span>
        ))}
    </Pill>
  );

  // Nothing at all for somebody a challenge can never name. Checked after the
  // hooks above, never before them — a bare return earlier would change how
  // many hooks run between renders.
  if (data && data.eligible === false) return null;

  return (
    <>
      {compact ? (
        <Tooltip title={label} arrow>
          <Box component="span">{pill}</Box>
        </Tooltip>
      ) : (
        pill
      )}

      <MenuPopover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        sx={{ p: 0, width: 340 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1.5, py: 1.25 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontSize: 14 }}>
              Your targets
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {formatUpdatedAt(updatedAt)}
            </Typography>
          </Box>

          <Tooltip title="Refresh" arrow>
            {/* Wrapped: a disabled button does not emit the events a Tooltip needs. */}
            <Box component="span">
              <IconButton size="small" onClick={refresh} disabled={loading} aria-label="Refresh">
                {loading ? (
                  <CircularProgress size={16} thickness={5} />
                ) : (
                  <Icon icon="mdi:refresh" width={18} />
                )}
              </IconButton>
            </Box>
          </Tooltip>
        </Stack>

        <Divider />

        {/* Fixed height with its own scroll, so a long list never grows the
            popover past the viewport. */}
        <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
          {rows.length === 0 ? (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {loading ? 'Loading…' : 'No challenges are set for you right now.'}
              </Typography>
              {!loading && (
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  Past challenges and analytics are in the full view.
                </Typography>
              )}
            </Box>
          ) : (
            rows.map((challenge, index) => (
              <Box key={challenge.id}>
                {index > 0 && <Divider />}
                <ChallengeRow challenge={challenge} />
              </Box>
            ))
          )}
        </Box>

        <Divider />

        <Box sx={{ p: 1 }}>
          <ButtonBase
            onClick={openTargets}
            disabled={opening}
            sx={{
              width: '100%',
              height: 34,
              borderRadius: 1,
              gap: 0.75,
              fontSize: 13,
              fontWeight: 600,
              color: 'primary.main',
              '&:hover': { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08) },
              '&.Mui-disabled': { opacity: 0.6 },
            }}
          >
            {opening ? (
              <CircularProgress size={15} thickness={5} />
            ) : (
              <Icon icon="mdi:open-in-new" width={16} />
            )}
            {opening ? 'Opening…' : 'Full view'}
          </ButtonBase>
        </Box>
      </MenuPopover>
    </>
  );
}

TargetsBadge.propTypes = {
  /** Icon-only, for narrow toolbars. */
  compact: PropTypes.bool,
};
